import { useEffect, useRef, useState } from "react";
import { Link, createFileRoute, useSearch } from "@tanstack/react-router";

import { ApiError, auth } from "~/lib/api";
import { AuthShell, primaryBtn } from "./auth.sign-in";

export const Route = createFileRoute("/auth/verify-email")({
  component: VerifyEmailPage,
  validateSearch: (raw): { token?: string } => ({
    token: typeof raw.token === "string" ? raw.token : undefined,
  }),
});

function VerifyEmailPage() {
  const { token } = useSearch({ from: "/auth/verify-email" });
  const [state, setState] = useState<"verifying" | "ok" | "error">("verifying");
  const verification = useRef<{ token: string; promise: Promise<void> } | null>(
    null,
  );
  const [errorMsg, setErrorMsg] = useState<string>("");

  useEffect(() => {
    if (!token) {
      setState("error");
      setErrorMsg("Verification link is missing the token.");
      return;
    }
    let alive = true;
    (async () => {
      try {
        // StrictMode replays effects; reuse the in-flight single-use-token request.
        if (verification.current?.token !== token) {
          verification.current = { token, promise: auth.verifyEmail(token) };
        }
        await verification.current.promise;
        if (alive) setState("ok");
      } catch (err) {
        if (!alive) return;
        setErrorMsg(
          err instanceof ApiError ? err.message : "Verification failed",
        );
        setState("error");
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  if (state === "verifying") {
    return (
      <AuthShell title="Verifying your email…">
        <p className="text-sm text-[color:var(--color-abyssal)]/70">
          Hang on a second.
        </p>
      </AuthShell>
    );
  }

  if (state === "ok") {
    return (
      <AuthShell title="You're in" subtitle="Your email is verified.">
        <Link to="/auth/sign-in" className={primaryBtn + " block text-center"}>
          Sign in
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Verification failed">
      <p className="text-sm text-[color:var(--color-coral)]">{errorMsg}</p>
      <p className="mt-4 text-sm text-[color:var(--color-abyssal)]/70">
        The link may have expired. Sign up again or request a new verification
        email.
      </p>
      <div className="mt-6 flex gap-3 text-sm">
        <Link
          to="/auth/sign-in"
          className="text-[color:var(--color-north-star)] hover:underline"
        >
          Sign in
        </Link>
        <Link
          to="/auth/sign-up"
          className="text-[color:var(--color-north-star)] hover:underline"
        >
          Create an account
        </Link>
      </div>
    </AuthShell>
  );
}
